import CONSTANTS from "core/constants";
import {
  Slot,
  SlotApiRes,
  SlotPaidStatus,
  SlotStatus,
  Template,
  TemplateNames,
} from "core/types/index";
import { differenceInHours, format } from "date-fns";
import { TFunction } from "i18next";
import { getTemplateTranslationKey } from "helpers/templates";

export const getStatus = (status: SlotStatus, isAdmin: boolean) => {
  return (
    (status === SlotStatus.NO_SHOW ||
      status === SlotStatus.NO_SHOW_ALL ||
      status === SlotStatus.ORGANIZER_NO_SHOW) &&
    isAdmin
  );
};

export const showColorStatus = (templateName: string | null) => {
  if (!templateName) return true;
  return ![
    TemplateNames.tech_test,
    TemplateNames.blocker,
    TemplateNames.work_hours,
    TemplateNames.supervision,
  ].some((name) => name === templateName);
};

export const getHoverStatus = (
  status: SlotStatus,
  isAdmin: boolean,
): boolean => {
  const isAdminAndNoShowStatus = getStatus(status, isAdmin);

  return (
    (status !== SlotStatus.CANCELED &&
      status !== SlotStatus.LATE_CANCELED &&
      isAdminAndNoShowStatus) ||
    status === SlotStatus.OPEN
  );
};

export const slotTime = (
  slot: Pick<Slot, "startsAt" | "endsAt" | "isDurationOneHour">,
) => {
  if (slot.isDurationOneHour) {
    return `${format(slot.startsAt, "HH:mm")}-${format(slot.endsAt, "HH:mm")}`;
  }

  return format(slot.startsAt, "HH:mm");
};

export const translatedHoverText = (slot: SlotApiRes, t: TFunction) => {
  if (slot.status === SlotStatus.BOOKED) {
    if (slot.payed === SlotPaidStatus.PAID) {
      return t("PAID");
    }

    if (slot.payed === SlotPaidStatus.NOT_PAID) {
      return t("NOT_PAID");
    }
  }

  return t(`${slot.status}`);
};

export const formattedSlotTitle = (
  slot: SlotApiRes,
  templateName: string | null,
  isDemo: boolean,
  t: TFunction,
) => {
  if (isDemo) {
    return CONSTANTS.DEMO;
  }

  if (
    slot.status === SlotStatus.OPEN ||
    templateName === TemplateNames.blocker
  ) {
    if (slot.bookingName) {
      if (
        typeof slot.bookingName !== "string" &&
        templateName === TemplateNames.blocker
      ) {
        return "Rename template";
      }

      return slot.bookingName;
    }

    return templateName ? t(getTemplateTranslationKey(templateName)) : "";
  }

  if (templateName === TemplateNames.tech_test) {
    const patientName = `${slot.firstName} ${slot.lastName}`;
    return `${t(getTemplateTranslationKey(templateName))}: ${patientName}`;
  }

  return `${slot.firstName} ${slot.lastName}`;
};

export const formatSlots = (
  t: TFunction,
  slots: SlotApiRes[],
  isAdmin: boolean,
  isDemo: boolean,
  templates: Template[],
) => {
  return slots
    .map<Slot>((slot) => {
      const startsAt = new Date(slot.startsAt * 1000).getTime();
      const endsAt = new Date(slot.endsAt * 1000).getTime();

      const showStatusOnHover = getHoverStatus(
        (slot.status ?? SlotStatus.OPEN) as SlotStatus,
        isAdmin,
      );

      const templateName =
        templates.find((y) => y.templateId === slot.templateId)?.name ?? null;

      const isDurationOneHour = differenceInHours(endsAt, startsAt) >= 1;

      return {
        ...slot,
        showStatusOnHover,
        showColorStatus: showColorStatus(templateName),
        isDurationOneHour,
        slotTime: slotTime({ startsAt, endsAt, isDurationOneHour }),
        formattedSlotTitle: formattedSlotTitle(slot, templateName, isDemo, t),
        translatedHoverText: translatedHoverText(slot, t),
      } as Slot;
    })
    .sort((a, b) => b.slotId - a.slotId);
};
